/**
 * Continuous, accessible enhancement for Horizon's announcement component.
 *
 * Horizon remains the no-JS fallback. Once its custom element is ready, this
 * module stops the slide timer, places every merchant-authored announcement in
 * one visual track, and adds an inert duplicate for a seamless loop. Screen
 * readers encounter the original messages once; the duplicate is hidden.
 */
const COMPONENT = 'announcement-bar-component';
const READY_ATTRIBUTE = 'data-tcs-announcement-ticker';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const loader = document.querySelector('script[data-tcs-announcement-ticker]');
const labels = {
  pause: loader?.dataset.pauseLabel || 'Pause announcements',
  play: loader?.dataset.playLabel || 'Play announcements',
};

const svg = (path) => {
  const namespace = 'http://www.w3.org/2000/svg';
  const icon = document.createElementNS(namespace, 'svg');
  icon.setAttribute('viewBox', '0 0 20 20');
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('focusable', 'false');
  const shape = document.createElementNS(namespace, 'path');
  shape.setAttribute('d', path);
  icon.append(shape);
  return icon;
};

const stripCloneSemantics = (clone) => {
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  clone.removeAttribute('id');
  clone.removeAttribute('ref');
  clone.removeAttribute('data-shopify-editor-block');
  clone.querySelectorAll('[id], [ref], [data-shopify-editor-block]').forEach((node) => {
    node.removeAttribute('id');
    node.removeAttribute('ref');
    node.removeAttribute('data-shopify-editor-block');
  });
};

const createToggle = (component) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'tcs-announcement-ticker__toggle button-unstyled';
  // The name alone carries the state ("Pause …" while moving, "Play …" while
  // paused). No aria-pressed: combined with a changing name it read
  // "Play slideshow, pressed" for a paused ticker (APG: change one, not both).
  button.setAttribute('aria-label', labels.pause);
  button.append(
    Object.assign(document.createElement('span'), {
      className: 'tcs-announcement-ticker__icon tcs-announcement-ticker__icon--pause',
    }),
    Object.assign(document.createElement('span'), {
      className: 'tcs-announcement-ticker__icon tcs-announcement-ticker__icon--play',
    }),
  );
  button.children[0].append(svg('M5 4h3v12H5zm7 0h3v12h-3z'));
  button.children[1].append(svg('M6 3.8v12.4L16 10z'));

  button.addEventListener('click', () => {
    const paused = !component.hasAttribute('paused');
    component.toggleAttribute('paused', paused);
    button.setAttribute('aria-label', paused ? labels.play : labels.pause);
  });
  return button;
};

const updateDuration = (component, group, itemCount, interval) => {
  if (!group.isConnected || group.scrollWidth === 0) return;
  const duration = Math.max(12, interval * itemCount * 2);
  component.style.setProperty('--tcs-announcement-duration', `${duration}s`);
};

const fillAndCloneTrack = (component, viewport, track, group, items, interval) => {
  track.querySelector(':scope > .tcs-announcement-ticker__clone')?.remove();
  group
    .querySelectorAll(':scope > .tcs-announcement-ticker__filler')
    .forEach((filler) => filler.remove());

  if (!group.isConnected || group.scrollWidth === 0) return;

  const maximumFillers = items.length * 20;
  let fillerCount = 0;
  while (group.scrollWidth < viewport.clientWidth && fillerCount < maximumFillers) {
    const filler = items[fillerCount % items.length].cloneNode(true);
    filler.classList.add('tcs-announcement-ticker__filler');
    stripCloneSemantics(filler);
    group.append(filler);
    fillerCount += 1;
  }

  const clone = group.cloneNode(true);
  clone.classList.add('tcs-announcement-ticker__clone');
  stripCloneSemantics(clone);
  track.append(clone);
  updateDuration(component, group, group.children.length, interval);
};

const enhance = (component) => {
  if (component.hasAttribute(READY_ATTRIBUTE)) return;

  const group = component.querySelector(':scope > .announcement-bar__slides');
  const items = [...(group?.children || [])];
  if (!group || items.length < 2) return;

  const interval = Number.parseFloat(component.getAttribute('autoplay')) || 5;
  component.suspend?.();
  component.removeAttribute('autoplay');
  component.removeAttribute('paused');
  // Horizon marks the bar aria-live="polite" only because its slides rotate. The
  // ticker shows every message at once and rebuilds hidden fillers/clones on
  // resize, so nothing here is a live update any more.
  component.removeAttribute('aria-live');

  // Horizon keeps its own visibilitychange and mouseleave handlers, which call
  // pause() and resume(). pause() sets the `paused` attribute the ticker CSS
  // honours, and resume() returns early once autoplay is gone, so a tab switch
  // would freeze the ticker while the toggle still reads "not paused". Shadow
  // both on this instance: only the toggle and the motion preference own the
  // attribute from here on. suspend() stays so any leftover slide timer stops.
  component.pause = () => component.suspend?.();
  component.resume = () => {};

  component.querySelector('slideshow-arrows')?.setAttribute('hidden', '');
  items.forEach((item) => item.setAttribute('aria-hidden', 'false'));

  const viewport = document.createElement('div');
  viewport.className = 'tcs-announcement-ticker__viewport';
  const track = document.createElement('div');
  track.className = 'tcs-announcement-ticker__track';
  group.classList.add('tcs-announcement-ticker__group');
  group.replaceWith(viewport);
  viewport.append(track);
  track.append(group);

  const toggle = createToggle(component);
  component.append(toggle);
  component.setAttribute(READY_ATTRIBUTE, '');

  const refresh = () => fillAndCloneTrack(component, viewport, track, group, items, interval);
  requestAnimationFrame(refresh);
  document.fonts?.ready.then(refresh);
  if ('ResizeObserver' in window) new ResizeObserver(refresh).observe(viewport);
};

const enhanceWithin = (root = document) => {
  const components = root.matches?.(COMPONENT)
    ? [root]
    : [...(root.querySelectorAll?.(COMPONENT) || [])];
  components.forEach(enhance);
};

customElements.whenDefined(COMPONENT).then(() => enhanceWithin());
document.addEventListener('shopify:section:load', (event) => enhanceWithin(event.target));

const resetMotionPreference = () => {
  document.querySelectorAll(`${COMPONENT}[${READY_ATTRIBUTE}]`).forEach((component) => {
    component.removeAttribute('paused');
    const button = component.querySelector('.tcs-announcement-ticker__toggle');
    button?.setAttribute('aria-label', labels.pause);
  });
};

if (REDUCED_MOTION.addEventListener) {
  REDUCED_MOTION.addEventListener('change', resetMotionPreference);
} else {
  REDUCED_MOTION.addListener(resetMotionPreference);
}
